import React, { createContext, useContext, useState, ReactNode } from 'react';
import { serverRoutes } from '../layout/serverRoutes';
import { ScanMetadata } from '../../../shared/src/data/scanMetadata';
import { useFetchFromServer } from '../api/fetchHook';
import { v4 as uuidv4 } from 'uuid';
import { ExecutionDetails, ExecutionStatus } from '../../../shared/src/data/executionDetails';
import { S3Link } from '../../../shared/src/data/s3Link';

interface ExecutionContextType {
    executions: Map<string, ExecutionDetails>;
    addExecution: (projectId: string, scanId: string, executionParams: S3Link) => Promise<void>;
    setExecutionArn: (executionId: string, newArn: string) => void;
  }

const ExecutionContext = createContext<ExecutionContextType | undefined>(undefined);

export const useExecutionProvider = () => {
  const context = useContext(ExecutionContext);
  if (!context) {
    throw new Error('useExecutionProvider must be used within an ExecutionProvider');
  }
  return context;
};

interface ExecutionProviderProps {
  children: ReactNode;
}

export const ExecutionProvider = ({ children }: ExecutionProviderProps) => {
  const [executions, setExecutions] = useState<Map<string, ExecutionDetails>>(new Map());
    const { fetchFromServer } = useFetchFromServer();
    
  const addExecution = async (projectId: string, scanId: string, executionParamsLink: S3Link): Promise<void> => {
    try {
      const scanMetadata = await getScanMetadata(scanId, fetchFromServer);
      const executionArn = uuidv4();
      const newExecution: ExecutionDetails = {
        id:"id",
        projectId,
        scanId,
        executionParamsLink,
        executionArn,
        projectName: scanMetadata.projectName,
        phase: scanMetadata.phase,
        status: ExecutionStatus.Pending,
      };

      setExecutions((prev) => new Map(prev).set(executionArn, newExecution));
    } catch (error) {
      console.error(`Failed to fetch scan metadata for scanId ${scanId}:`, error);
    }
  };

  // Function to update the executionArn for a specific execution
  const setExecutionArn = (executionId: string, newArn: string) => {
    setExecutions((prev) => {
      const updatedExecutions = new Map(prev);

      // Check if executionId exists before updating
      const execution = updatedExecutions.get(executionId);
      if (execution) {
        updatedExecutions.set(executionId, { ...execution, executionArn: newArn });
      } else {
        console.error(`Execution with ID ${executionId} not found`);
      }

      return updatedExecutions;
    });
  };

  return (
    <ExecutionContext.Provider value={{ executions, addExecution, setExecutionArn }}>
      {children}
    </ExecutionContext.Provider>
  );
};


  export async function getScanMetadata(
    scanId: string,
    fetchFromServer: (path: string, init?: RequestInit) => Promise<Response>,
  ): Promise<ScanMetadata> {
    try {
      const response = await fetchFromServer(serverRoutes.api.scansMetadata.get(scanId))

      if (!response || !response.ok) throw new Error('Failed to get scan metadata')

      const scanMetadata: ScanMetadata = await response.json();
      return scanMetadata;
    } catch (error) {
      console.error(error)
      return {} as ScanMetadata;
    }
  }
